// FILE: src/pages/Clients/ClientsPage.jsx
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { clientsAPI } from '../../api/services';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import ClientFormModal from './ClientFormModal';
import '../../components/UI/UI.css';
import './Clients.css';

const initials = (first, last) =>
  `${first?.[0] || ''}${last?.[0] || ''}`.toUpperCase();

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-US', { year:'numeric', month:'short', day:'numeric' }) : '—';

export default function ClientsPage() {
  const navigate    = useNavigate();
  const { hasRole } = useAuth();
  const canEdit     = hasRole('Admin', 'CaseManager');

  const [clients,    setClients]    = useState([]);
  const [filtered,   setFiltered]   = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [search,     setSearch]     = useState('');
  const [showModal,  setShowModal]  = useState(false);
  const [editing,    setEditing]    = useState(null);
  const [deleting,   setDeleting]   = useState(null);
  const [delLoading, setDelLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await clientsAPI.getAll();
      setClients(res.data.data);
      setFiltered(res.data.data);
    } catch { toast.error('Failed to load clients.'); }
    finally   { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!search.trim()) { setFiltered(clients); return; }
    const q = search.toLowerCase();
    setFiltered(clients.filter(c =>
      `${c.FirstName} ${c.LastName}`.toLowerCase().includes(q) ||
      (c.MedicaidID   || '').toLowerCase().includes(q) ||
      (c.PhoneNumber  || '').includes(q) ||
      (c.Email        || '').toLowerCase().includes(q) ||
      (c.ServiceType  || '').toLowerCase().includes(q)
    ));
  }, [search, clients]);

  const handleDelete = async () => {
    if (!deleting) return;
    setDelLoading(true);
    try {
      await clientsAPI.delete(deleting.ID);
      toast.success(`${deleting.FirstName} ${deleting.LastName} deleted.`);
      setDeleting(null);
      load();
    } catch { toast.error('Delete failed.'); }
    finally   { setDelLoading(false); }
  };

  const colors = ['#1a4d8c','#059669','#d97706','#7c3aed','#db2777','#0891b2'];
  const getColor = (id) => colors[id % colors.length];

  return (
    <div>
      <div className="page-header">
        <div className="page-header-left">
          <h1>Clients</h1>
          <p>{clients.length} total clients</p>
        </div>
        <div style={{ display:'flex', gap:10 }}>
          <div className="search-bar">
            <span className="search-icon">🔍</span>
            <input className="form-control" placeholder="Search name, Medicaid ID, phone…"
              value={search} onChange={e => setSearch(e.target.value)}
              style={{ paddingLeft:36, minWidth:260 }} />
          </div>
          {canEdit && (
            <button className="btn btn-primary"
              onClick={() => { setEditing(null); setShowModal(true); }}>
              ➕ Add Client
            </button>
          )}
        </div>
      </div>

      <div className="stats-grid-4" style={{ marginBottom:20 }}>
        {[
          { label:'Total Clients',  value:clients.length,                                          icon:'👥' },
          { label:'Medicaid',       value:clients.filter(c=>c.MedicaidID).length,                  icon:'🏥' },
          { label:'Medicare',       value:clients.filter(c=>c.MedicareID).length,                  icon:'💊' },
          { label:'Private Ins.',   value:clients.filter(c=>!c.MedicaidID&&!c.MedicareID).length,  icon:'📋' },
        ].map(s => (
          <div className="stat-card" key={s.label}>
            <div className="stat-card-header">
              <span className="stat-card-label">{s.label}</span>
              <span style={{ fontSize:20 }}>{s.icon}</span>
            </div>
            <div className="stat-card-value">{s.value}</div>
          </div>
        ))}
      </div>

      <div className="card" style={{ padding:0 }}>
        {loading ? (
          <div className="loading-state">⏳ Loading clients…</div>
        ) : filtered.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">👥</div>
            <div className="empty-state-text">
              {search ? 'No clients match your search.' : 'No clients yet.'}
            </div>
            {!search && canEdit && (
              <button className="btn btn-primary" style={{ marginTop:14 }}
                onClick={() => { setEditing(null); setShowModal(true); }}>
                ➕ Add First Client
              </button>
            )}
          </div>
        ) : (
          <div className="table-wrap">
            <table className="ec-table">
              <thead>
                <tr>
                  <th>Client</th><th>Contact</th><th>Insurance</th>
                  <th>Service</th><th>Coordinator</th><th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(c => (
                  <tr key={c.ID}>
                    <td>
                      <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                        <div className="client-avatar" style={{ background:getColor(c.ID) }}>
                          {initials(c.FirstName, c.LastName)}
                        </div>
                        <div>
                          <div style={{ fontWeight:600, color:'var(--gray-800)' }}>
                            {c.FirstName} {c.MiddleName ? c.MiddleName+' ':''}{c.LastName}
                          </div>
                          <div style={{ fontSize:'0.75rem', color:'var(--gray-400)' }}>
                            DOB: {fmtDate(c.DateOfBirth)}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div style={{ fontSize:'0.82rem' }}>{c.CellNumber || c.PhoneNumber || '—'}</div>
                      <div style={{ fontSize:'0.75rem', color:'var(--gray-400)' }}>{c.Email || ''}</div>
                    </td>
                    <td>
                      {c.MedicaidID ? (
                        <div>
                          <span className="badge badge-success">Medicaid</span>
                          <div style={{ fontSize:'0.72rem', color:'var(--gray-400)', marginTop:3 }}>{c.MedicaidID}</div>
                        </div>
                      ) : c.MedicareID ? (
                        <div>
                          <span className="badge badge-info">Medicare</span>
                          <div style={{ fontSize:'0.72rem', color:'var(--gray-400)', marginTop:3 }}>{c.MedicareID}</div>
                        </div>
                      ) : (
                        <span className="badge badge-gray">{c.InsuranceProvider || 'Private'}</span>
                      )}
                    </td>
                    <td><span className="badge badge-primary">{c.ServiceType || '—'}</span></td>
                    <td style={{ fontSize:'0.82rem', color:'var(--gray-600)' }}>{c.Coordinator || '—'}</td>
                    <td>
                      <div style={{ display:'flex', gap:6 }}>
                        <button className="btn btn-sm btn-secondary" title="View"
                          onClick={() => navigate(`/clients/${c.ID}`)}>👁️</button>
                        {canEdit && (
                          <>
                            <button className="btn btn-sm btn-secondary" title="Edit"
                              onClick={() => { setEditing(c); setShowModal(true); }}>✏️</button>
                            <button className="btn btn-sm btn-danger" title="Delete"
                              onClick={() => setDeleting(c)}>🗑️</button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <ClientFormModal client={editing} onClose={() => setShowModal(false)}
          onSaved={() => { setShowModal(false); load(); }} />
      )}

      {deleting && (
        <div className="modal-overlay">
          <div className="modal modal-sm">
            <div className="modal-header">
              <span className="modal-title">🗑️ Delete Client</span>
              <button className="modal-close" onClick={() => setDeleting(null)}>✕</button>
            </div>
            <div className="modal-body">
              <p style={{ color:'var(--gray-600)' }}>
                Are you sure you want to delete <strong>{deleting.FirstName} {deleting.LastName}</strong>?
                This cannot be undone.
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setDeleting(null)}>Cancel</button>
              <button className="btn btn-danger" onClick={handleDelete} disabled={delLoading}>
                {delLoading ? 'Deleting…' : 'Yes, Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}